import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import ConsultantEditModal from './ConsultantEditModal';
import './ManageVastuConsultations.css'; // Reusing existing tables styles

const ManageVastuConsultants = () => {
  const [consultants, setConsultants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState(null);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getVastuAdminConsultants();
      setConsultants(response.data || []);
    } catch (err) {
      toast.error('Failed to load consultants');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleOpenAddModal = () => {
    setSelectedConsultant(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (consultant) => {
    setSelectedConsultant(consultant);
    setIsModalOpen(true);
  };

  // --- THIS WAS THE MISSING FUNCTION ---
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedConsultant(null);
  };
  // -------------------------------------

  const handleSaveConsultant = async (formData) => {
    try {
      if (selectedConsultant) {
        // Edit Mode: Update Profile
        await apiService.updateVastuAdminConsultantProfile(selectedConsultant._id, formData);

        // --- FIX FOR BUG 1 ---
        // Check if a new fee category was typed into the form
        if (formData.get('consultationName') && formData.get('consultationPrice')) {
            // We use the specific endpoint we built for adding a fee category to an EXISTING consultant
             await apiService.updateVastuAdminConsultant(selectedConsultant._id, {
                consultationTypes: [
                  ...(selectedConsultant.consultationTypes || []),
                  { 
                    name: formData.get('consultationName'), 
                    price: Number(formData.get('consultationPrice')), 
                    durationMinutes: Number(formData.get('consultationDuration') || 45), 
                    isActive: true 
                  }
                ]
             });
        }
        // -----------------------
        toast.success('Consultant profile updated!');
      } else {
        // Add Mode: First assign role, then create consultant
        const roleResponse = await apiService.assignVastuConsultantRole(formData.get('email'));
        const linkedUserId = roleResponse.data?.user?.id;
        if (!linkedUserId) throw new Error('Could not link to user account.');

        formData.append('linkedAnandmayaUserId', linkedUserId);

        // We have to convert FormData back to JSON for the creation endpoint because it doesn't support file uploads initially
        const payload = Object.fromEntries(formData.entries());
        payload.languages = payload.languages.split(',').map(s => s.trim());
        payload.specializations = payload.specializations.split(',').map(s => s.trim());
        payload.consultationTypes = [{ 
            name: payload.consultationName, 
            price: Number(payload.consultationPrice), 
            durationMinutes: Number(payload.consultationDuration || 45), 
            isActive: true 
        }];

        const createRes = await apiService.createVastuAdminConsultant(payload);

        // If they added a photo during creation, upload it now
        if (formData.get('profilePhoto') && formData.get('profilePhoto').size > 0) {
          const photoData = new FormData();
          photoData.append('profilePhoto', formData.get('profilePhoto'));
          await apiService.updateVastuAdminConsultantProfile(createRes.data._id, photoData);
        }
        toast.success('Consultant added successfully!');
      }
      loadData();
      handleCloseModal(); // Safely closes the modal and resets selected consultant
    } catch (err) {
      toast.error(err.message || 'Error saving consultant');
    }
  };

  const handleRemoveFee = async (consultantId, feeId) => {
    if (window.confirm("Are you sure you want to remove this fee category?")) {
      try {
        await apiService.removeVastuConsultantFeeCategory(consultantId, feeId);
        toast.success("Fee removed");

        // Update the selected consultant state so the modal refreshes instantly
        const updatedResponse = await apiService.getVastuAdminConsultants();
        setConsultants(updatedResponse.data || []);
        const refreshedConsultant = updatedResponse.data.find(c => c._id === consultantId);
        setSelectedConsultant(refreshedConsultant);

      } catch (err) {
        toast.error("Failed to remove fee");
      }
    }
  };

  const toggleStatus = async (consultant) => {
    try {
      await apiService.updateVastuAdminConsultant(consultant._id, { isActive: !consultant.isActive });
      loadData();
      toast.success('Status updated');
    } catch (err) { toast.error('Failed to update status'); }
  };

  if (isLoading) return <div className="loading-spinner">Loading Vastu consultants...</div>;

  return (
    <div className="manage-vastu-container">
      <div className="header-section">
        <div>
          <h2>Vastu Consultants</h2>
          <p>Manage expert profiles, bios, and consultation fees.</p>
        </div>
        <button
          className="primary-btn"
          onClick={handleOpenAddModal}
          aria-label="Add a new Vastu consultant"
        >
          <i className="fas fa-user-plus" aria-hidden="true"></i>
          <span>Add Consultant</span>
        </button>
      </div>

      <div className="vastu-table-wrapper">
        <table className="vastu-table">
          <thead>
            <tr>
              <th>Profile</th>
              <th>Contact Info</th>
              <th>Experience & Specs</th>
              <th>Fee Categories</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {consultants.map((consultant) => (
              <tr key={consultant._id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#eee', overflow: 'hidden' }}>
                      {consultant.profilePhoto?.storagePath ? (
                        <img src={consultant.profilePhoto.storagePath} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                          <i className="fas fa-user"></i>
                        </div>
                      )}
                    </div>
                    <strong>{consultant.displayName}</strong>
                  </div>
                </td>
                <td>{consultant.email}<br /><small className="text-muted">{consultant.phone || 'No phone'}</small></td>
                <td>
                  {consultant.experienceYears || 0} years <br />
                  <small className="text-muted">{(consultant.specializations || []).join(', ')}</small>
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {(consultant.consultationTypes || []).map(fee => (
                      <span key={fee._id} style={{ fontSize: '0.8rem', background: '#f0fbf7', color: 'var(--green)', padding: '2px 8px', borderRadius: '4px', border: '1px solid #a8dccb' }}>
                        {fee.name}: ₹{fee.price} ({fee.durationMinutes} mins)
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <span className={`status-badge status-${consultant.isActive ? 'paid' : 'cancelled'}`}>
                    {consultant.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="action-cell">
                  <button className="action-btn" onClick={() => handleOpenEditModal(consultant)} title="Edit Profile">
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="action-btn" onClick={() => toggleStatus(consultant)} title={consultant.isActive ? 'Deactivate' : 'Activate'}>
                    <i className={`fas ${consultant.isActive ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConsultantEditModal
        consultant={selectedConsultant}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveConsultant}
        onRemoveFee={handleRemoveFee}
      />
    </div>
  );
};

export default ManageVastuConsultants;